import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  compareAssessmentReports,
  fetchAssessmentReport,
  type AssessmentReport
} from "@/services/repositories/assessment-repository";
import type { Assessment } from "@/types/assessment";
import type { ScaleLibrary } from "@/types/scale";
import {
  sortAnalysisPoints,
  type AssessmentAnalysisPoint
} from "@/utils/report-analysis";
import type { DomainCode } from "@/constants/domains";

export function useReportAnalysisPoints(
  assessments: Assessment[],
  scaleLibrary: ScaleLibrary,
): {
  points: AssessmentAnalysisPoint[];
  isLoading: boolean;
  isError: boolean;
} {
  void scaleLibrary;
  const assessmentIds = assessments.map((assessment) => assessment.id);
  const reportsQuery = useQuery({
    queryKey: ["assessmentReports", assessmentIds],
    enabled: assessmentIds.length > 0,
    queryFn: async () =>
      assessmentIds.length === 1
        ? [await fetchAssessmentReport(assessmentIds[0])]
        : compareAssessmentReports(assessmentIds),
    staleTime: 60 * 1000
  });

  const points = useMemo(
    () =>
      sortAnalysisPoints(
        (reportsQuery.data ?? []).map(toAnalysisPoint),
      ),
    [reportsQuery.data],
  );

  return {
    points,
    isLoading: reportsQuery.isLoading,
    isError: reportsQuery.isError
  };
}

function toAnalysisPoint(report: AssessmentReport): AssessmentAnalysisPoint {
  const summary = report.summary;
  const domainStats = summary.domains.map((domain) => ({
    code: domain.code as DomainCode,
    name: domain.name,
    totalEntries: domain.assessedCount,
    completed: domain.assessedCount,
    score: domain.score,
    maxScore: domain.maxScore,
    average: domain.average,
    scorePercent: domain.scorePercent / 100,
    completionPercent: domain.assessedCount > 0 ? 1 : 0
  }));
  const subdomainStats = summary.subdomains.map((subdomain) => ({
    key: subdomain.key,
    domainCode: subdomain.domainCode as DomainCode,
    label: subdomain.name,
    name: subdomain.name,
    totalEntries: subdomain.assessedCount,
    completed: subdomain.assessedCount,
    score: subdomain.score,
    maxScore: subdomain.maxScore,
    average: subdomain.average,
    scorePercent: subdomain.scorePercent / 100,
    completionPercent: subdomain.assessedCount > 0 ? 1 : 0
  }));

  return {
    assessment: report.assessment,
    entries: [],
    observations: report.observations,
    domainStats,
    subdomainStats,
    totalScore: summary.total.score,
    totalMaxScore: summary.total.maxScore,
    totalAverage: summary.total.average,
    totalCompleted: summary.total.assessedCount,
    totalCatalogEntries: summary.total.assessedCount,
    totalScorePercent: summary.total.scorePercent / 100,
    totalCompletionPercent: summary.total.assessedCount > 0 ? 1 : 0,
    hasScores: summary.total.assessedCount > 0
  };
}
